import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Save, 
  Home, 
  User, 
  Briefcase, 
  Mail,
  Image,
  RefreshCw,
  Eye
} from 'lucide-react';
import { useAuth, API } from '@/App';

const SiteContentPage = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('homepage');
  
  // Content state for each page - pre-populated with current live content
  const [homepage, setHomepage] = useState({
    badge_text: '40+ Years of Marketing Excellence',
    hero_title: 'Marketing That Moves the Needle',
    hero_subtitle: 'Enterprise-level marketing expertise with a personal, collaborative approach. We help businesses sharpen their message, strengthen their brand, and execute marketing that delivers real results.',
    hero_cta_text: 'Get a Free Consultation',
    hero_cta_link: '/contact',
    stats: [
      { value: '40+', label: 'Years Experience' },
      { value: '24+', label: 'Years at VELUX' },
      { value: '$M+', label: 'Budgets Managed' },
      { value: '12+', label: 'Team Members Led' }
    ],
    services_title: 'Services That Drive Results',
    services_subtitle: 'Comprehensive marketing solutions backed by decades of experience',
    cta_title: 'Ready to Elevate Your Marketing?',
    cta_subtitle: "Let's discuss how we can help you sharpen your message, strengthen your brand, and achieve measurable results.",
    cta_button_text: 'Get a Free Consultation'
  });

  const [about, setAbout] = useState({
    badge_text: '40+ Years of Excellence',
    page_title: 'About SPT Digital Marketing',
    page_subtitle: 'Enterprise-level marketing expertise with a personal, collaborative approach — helping clients sharpen their message, strengthen their brand, and execute marketing that actually moves the needle.',
    founder_name: 'Channing Hoyle',
    founder_title: 'Founder, SPT Digital Marketing',
    founder_image: '',
    founder_bio: `Channing Hoyle is the founder of SPT Digital Marketing, bringing more than <strong>40 years of hands-on experience</strong> in brand building, creative direction, and full-scale marketing leadership.

After retiring as the <strong>Senior Director of Marketing Communications for VELUX North America</strong>, Channing launched SPT to help businesses elevate their marketing with the same strategic discipline, creative excellence, and operational precision he brought to some of the most recognized brands in the home improvement industry.

Throughout his career, Channing has led large in-house creative teams, managed multi-million dollar budgets, and built integrated marketing programs that consistently drove category growth, brand awareness, and measurable sales results.`,
    founder_quote: 'At SPT Digital Marketing, we blend enterprise-level marketing experience with a personal, collaborative approach — helping clients sharpen their message, strengthen their brand, and execute marketing that actually moves the needle.',
    stats: [
      { value: '40+', label: 'Years Experience' },
      { value: '24+', label: 'Years at VELUX' },
      { value: '$M+', label: 'Budgets Managed' },
      { value: '12+', label: 'Team Led' }
    ],
    expertise: [
      { title: 'Brand Strategy', description: 'Strategic brand positioning that sets you apart from the competition' },
      { title: 'Digital Marketing', description: 'Full-funnel marketing programs that drive measurable results' },
      { title: 'Consumer & Trade Advertising', description: 'Integrated campaigns across all channels' },
      { title: 'Influencer Programs', description: 'Strategic partnerships that amplify your brand reach' },
      { title: 'Product Placement', description: 'Getting your products in front of the right audiences' },
      { title: 'Web Design & Development', description: 'Custom websites and web applications' },
      { title: 'Paid Search & SEO', description: 'Driving qualified traffic and leads' },
      { title: 'Event Strategy', description: 'Memorable experiences that connect brands with audiences' }
    ],
    experience: [
      { role: 'Senior Director of Marketing Communications', company: 'VELUX North America', duration: '24+ Years', highlights: 'Oversaw national consumer and trade advertising\nLed digital strategy and influencer programs\nManaged 12-person internal creative team\nDeveloped integrated campaigns generating qualified leads' },
      { role: 'Manager, Creative Services', company: 'Cooper Hand Tools', duration: 'Previous Role', highlights: 'Managed multi-million dollar creative services budget\nLed 11-person team for catalogs, packaging (40,000 SKUs)\nOversaw co-op advertising and trade shows\nSupported brands including Crescent, Lufkin, Nicholson' },
      { role: 'Creative Director', company: 'McDaniel & Hoyle Communications', duration: 'Previous Role', highlights: 'Led design for literature, packaging, merchandisers\nClients: John Deere/Homelite, Rubbermaid, Freud\nLogo development and brand identity systems\nFull catalog design and production' },
      { role: 'Art Director', company: 'Robert Bosch Tool Corporation', duration: 'Previous Role', highlights: 'Managed 7-person creative team\nProduced domestic and international packaging (25,000 SKUs)\nVermont American and private label brands\nMerchandising and literature systems' }
    ],
    approach: []
  });

  const [services, setServices] = useState({
    badge_text: 'What We Do',
    page_title: 'Services That Drive Results',
    page_subtitle: 'Comprehensive marketing services backed by 40+ years of experience. From brand strategy to digital execution, we help businesses elevate their marketing and achieve measurable growth.',
    services_list: [
      { title: 'Brand Strategy & Identity', icon: 'palette', description: 'Strategic brand positioning and visual identity systems that differentiate you from competitors and resonate with your target audience.', features: 'Brand positioning and messaging\nVisual identity systems\nBrand guidelines and standards\nBrand architecture\nNaming and tagline development' },
      { title: 'Web Design & Development', icon: 'globe', description: 'Custom websites and web applications built to convert visitors into customers with modern, responsive design and SEO best practices.', features: 'Custom website design\nWeb application development\nE-commerce solutions\nCMS implementation\nWebsite optimization & maintenance' },
      { title: 'Digital Marketing', icon: 'trending-up', description: 'Full-funnel digital marketing programs that drive measurable results, from awareness through conversion and retention.', features: 'Digital strategy development\nCampaign management\nMarketing automation\nAnalytics and reporting\nConversion optimization' },
      { title: 'SEO & Paid Search', icon: 'search', description: 'Drive qualified traffic to your website through organic search optimization and strategic paid search campaigns.', features: 'Technical SEO audits\nOn-page optimization\nContent strategy for SEO\nGoogle Ads management\nLocal SEO' },
      { title: 'Content Marketing', icon: 'users', description: 'Compelling content that builds authority, engages your audience, and supports your business objectives across all channels.', features: 'Content strategy\nBlog and article writing\nCase studies and white papers\nEmail newsletter content\nSocial media content' },
      { title: 'Advertising', icon: 'megaphone', description: 'Integrated advertising campaigns across consumer and trade channels that build awareness and drive action.', features: 'Campaign strategy and planning\nCreative development\nMedia planning and buying\nTrade advertising\nCo-op program management' },
      { title: 'Packaging & Merchandising', icon: 'package', description: 'Packaging design and merchandising systems that command shelf presence and communicate your brand effectively.', features: 'Packaging design\nPoint-of-sale displays\nRetail merchandising systems\nProduct catalog design\nTrade show materials' },
      { title: 'Event Marketing', icon: 'calendar', description: 'Strategic event planning and trade show presence that creates memorable brand experiences and generates leads.', features: 'Trade show strategy\nBooth design and production\nEvent marketing campaigns\nLead capture and follow-up\nPost-event analysis' }
    ],
    process_steps: [
      { number: '01', title: 'Discovery', description: 'We start by understanding your business, goals, competitive landscape, and target audience.' },
      { number: '02', title: 'Strategy', description: 'We develop a tailored strategy aligned with your objectives and budget.' },
      { number: '03', title: 'Execution', description: 'We bring the strategy to life with precision, creativity, and attention to detail.' },
      { number: '04', title: 'Optimization', description: 'We measure results, learn from data, and continuously improve performance.' }
    ],
    industries: [
      { name: 'Home Improvement & Building Products' },
      { name: 'Manufacturing & Industrial' },
      { name: 'Professional Services' },
      { name: 'B2B & Trade Marketing' },
      { name: 'Consumer Products' }
    ],
    why_choose_title: 'Why Work With SPT Digital Marketing?',
    why_choose_items: []
  });

  const [contact, setContact] = useState({
    badge_text: "Let's Connect",
    page_title: 'Get in Touch',
    page_subtitle: "Ready to elevate your marketing? Let's discuss how we can help you sharpen your message, strengthen your brand, and achieve real results.",
    location: 'Southport, NC',
    email: 'info@sptdigitalmarketing.com',
    phone: '',
    response_time: 'Within 24-48 hours',
    form_title: 'Send Us a Message',
    success_title: 'Thank You for Reaching Out!',
    success_message: "We've received your message and will get back to you within 24-48 hours. In the meantime, feel free to explore our work and insights.",
    faqs: [
      { question: 'What types of businesses do you work with?', answer: 'We work with businesses of all sizes across various industries, with particular expertise in home improvement, building products, manufacturing, and professional services.' },
      { question: 'How do you typically start a new project?', answer: 'Every engagement begins with a discovery conversation to understand your goals, challenges, and market position. From there, we develop a tailored strategy and clear scope of work.' },
      { question: 'What makes SPT Digital Marketing different?', answer: 'We combine 40+ years of enterprise-level marketing experience with the personal attention of a boutique agency. You get strategic depth and creative excellence with direct access to senior leadership.' },
      { question: 'What are your typical project timelines?', answer: 'Timelines vary based on project scope. Brand strategy projects typically take 6-12 weeks, websites 8-16 weeks, and ongoing marketing engagements are structured around monthly or quarterly cycles.' }
    ]
  });

  const [footer, setFooter] = useState({
    tagline: 'Enterprise-level marketing expertise with a personal, collaborative approach. Helping businesses elevate their marketing since 2024.',
    copyright_text: 'SPT Digital Marketing. All rights reserved.',
    social_links: []
  });

  useEffect(() => {
    fetchAllContent();
  }, []);

  const fetchAllContent = async () => {
    try {
      const response = await axios.get(`${API}/site-content`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      
      if (data.homepage) setHomepage(prev => ({ ...prev, ...data.homepage }));
      if (data.about) setAbout(prev => ({ ...prev, ...data.about }));
      if (data.services) setServices(prev => ({ ...prev, ...data.services }));
      if (data.contact) setContact(prev => ({ ...prev, ...data.contact }));
      if (data.footer) setFooter(prev => ({ ...prev, ...data.footer }));
    } catch (err) {
      console.log('No existing content, using defaults');
    } finally {
      setLoading(false);
    }
  };

  const saveContent = async (section, data) => {
    setSaving(true);
    try {
      await axios.put(`${API}/site-content/${section}`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${section.charAt(0).toUpperCase() + section.slice(1)} content saved!`);
    } catch (err) {
      toast.error('Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const handleArrayChange = (setter, field, index, key, value) => {
    setter(prev => {
      const newArray = [...(prev[field] || [])];
      if (!newArray[index]) newArray[index] = {};
      newArray[index] = { ...newArray[index], [key]: value };
      return { ...prev, [field]: newArray };
    });
  };

  const addArrayItem = (setter, field, template) => {
    setter(prev => ({
      ...prev,
      [field]: [...(prev[field] || []), template]
    }));
  };

  const removeArrayItem = (setter, field, index) => {
    setter(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1F3C4F]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Site Content Manager</h1>
          <p className="text-slate-600">Edit all text and images on your public website</p>
        </div>
        <a href="/" target="_blank" rel="noopener noreferrer">
          <Button variant="outline">
            <Eye className="w-4 h-4 mr-2" />
            Preview Site
          </Button>
        </a>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-5 w-full max-w-2xl">
          <TabsTrigger value="homepage" className="flex items-center gap-2">
            <Home className="w-4 h-4" />
            <span className="hidden sm:inline">Homepage</span>
          </TabsTrigger>
          <TabsTrigger value="about" className="flex items-center gap-2">
            <User className="w-4 h-4" />
            <span className="hidden sm:inline">About</span>
          </TabsTrigger>
          <TabsTrigger value="services" className="flex items-center gap-2">
            <Briefcase className="w-4 h-4" />
            <span className="hidden sm:inline">Services</span>
          </TabsTrigger>
          <TabsTrigger value="contact" className="flex items-center gap-2">
            <Mail className="w-4 h-4" />
            <span className="hidden sm:inline">Contact</span>
          </TabsTrigger>
          <TabsTrigger value="footer" className="flex items-center gap-2">
            <Image className="w-4 h-4" />
            <span className="hidden sm:inline">Footer</span>
          </TabsTrigger>
        </TabsList>

        {/* HOMEPAGE TAB */}
        <TabsContent value="homepage" className="mt-6">
          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle>Homepage Content</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Hero Section */}
              <div className="border-b pb-6">
                <h3 className="font-semibold text-lg mb-4">Hero Section</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Hero Title</Label>
                    <Input
                      value={homepage.hero_title}
                      onChange={(e) => setHomepage(prev => ({ ...prev, hero_title: e.target.value }))}
                      placeholder="Marketing That Moves the Needle"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Hero CTA Button Text</Label>
                    <Input
                      value={homepage.hero_cta_text}
                      onChange={(e) => setHomepage(prev => ({ ...prev, hero_cta_text: e.target.value }))}
                      placeholder="Start a Conversation"
                    />
                  </div>
                </div>
                <div className="space-y-2 mt-4">
                  <Label>Hero Subtitle</Label>
                  <Textarea
                    value={homepage.hero_subtitle}
                    onChange={(e) => setHomepage(prev => ({ ...prev, hero_subtitle: e.target.value }))}
                    placeholder="Enterprise-level marketing expertise..."
                    rows={3}
                  />
                </div>
              </div>

              {/* Stats Section */}
              <div className="border-b pb-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold text-lg">Stats/Trust Badges</h3>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => addArrayItem(setHomepage, 'stats', { value: '', label: '' })}
                  >
                    Add Stat
                  </Button>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  {(homepage.stats || []).map((stat, idx) => (
                    <div key={idx} className="flex gap-2 items-start">
                      <div className="flex-1 space-y-2">
                        <Input
                          value={stat.value || ''}
                          onChange={(e) => handleArrayChange(setHomepage, 'stats', idx, 'value', e.target.value)}
                          placeholder="40+"
                        />
                        <Input
                          value={stat.label || ''}
                          onChange={(e) => handleArrayChange(setHomepage, 'stats', idx, 'label', e.target.value)}
                          placeholder="Years Experience"
                        />
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => removeArrayItem(setHomepage, 'stats', idx)}
                        className="text-red-500"
                      >
                        ×
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA Section */}
              <div>
                <h3 className="font-semibold text-lg mb-4">CTA Section</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>CTA Title</Label>
                    <Input
                      value={homepage.cta_title}
                      onChange={(e) => setHomepage(prev => ({ ...prev, cta_title: e.target.value }))}
                      placeholder="Ready to Elevate Your Marketing?"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>CTA Button Text</Label>
                    <Input
                      value={homepage.cta_button_text}
                      onChange={(e) => setHomepage(prev => ({ ...prev, cta_button_text: e.target.value }))}
                      placeholder="Get Started Today"
                    />
                  </div>
                </div>
                <div className="space-y-2 mt-4">
                  <Label>CTA Subtitle</Label>
                  <Textarea
                    value={homepage.cta_subtitle}
                    onChange={(e) => setHomepage(prev => ({ ...prev, cta_subtitle: e.target.value }))}
                    placeholder="Let's discuss how we can help..."
                    rows={2}
                  />
                </div>
              </div>

              <Button 
                onClick={() => saveContent('homepage', homepage)} 
                disabled={saving}
                className="bg-[#1F3C4F] hover:bg-[#152a38]"
              >
                {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Homepage Content
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ABOUT TAB */}
        <TabsContent value="about" className="mt-6">
          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle>About Page Content</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Page Header */}
              <div className="border-b pb-6">
                <h3 className="font-semibold text-lg mb-4">Page Header</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Page Title</Label>
                    <Input
                      value={about.page_title}
                      onChange={(e) => setAbout(prev => ({ ...prev, page_title: e.target.value }))}
                      placeholder="About SPT Digital Marketing"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Badge Text</Label>
                    <Input
                      value={about.badge_text}
                      onChange={(e) => setAbout(prev => ({ ...prev, badge_text: e.target.value }))}
                      placeholder="40+ Years of Excellence"
                    />
                  </div>
                </div>
                <div className="space-y-2 mt-4">
                  <Label>Page Subtitle</Label>
                  <Textarea
                    value={about.page_subtitle}
                    onChange={(e) => setAbout(prev => ({ ...prev, page_subtitle: e.target.value }))}
                    placeholder="Enterprise-level marketing expertise..."
                    rows={2}
                  />
                </div>
              </div>

              {/* Founder Section */}
              <div className="border-b pb-6">
                <h3 className="font-semibold text-lg mb-4">Founder Information</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Founder Name</Label>
                    <Input
                      value={about.founder_name}
                      onChange={(e) => setAbout(prev => ({ ...prev, founder_name: e.target.value }))}
                      placeholder="Channing Hoyle"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Founder Title</Label>
                    <Input
                      value={about.founder_title}
                      onChange={(e) => setAbout(prev => ({ ...prev, founder_title: e.target.value }))}
                      placeholder="Founder, SPT Digital Marketing"
                    />
                  </div>
                </div>
                <div className="space-y-2 mt-4">
                  <Label>Founder Image URL</Label>
                  <Input
                    value={about.founder_image}
                    onChange={(e) => setAbout(prev => ({ ...prev, founder_image: e.target.value }))}
                    placeholder="https://example.com/photo.jpg"
                  />
                </div>
                <div className="space-y-2 mt-4">
                  <Label>Founder Bio (HTML supported)</Label>
                  <Textarea
                    value={about.founder_bio}
                    onChange={(e) => setAbout(prev => ({ ...prev, founder_bio: e.target.value }))}
                    placeholder="Channing Hoyle is the founder of SPT Digital Marketing..."
                    rows={6}
                  />
                </div>
                <div className="space-y-2 mt-4">
                  <Label>Founder Quote</Label>
                  <Textarea
                    value={about.founder_quote}
                    onChange={(e) => setAbout(prev => ({ ...prev, founder_quote: e.target.value }))}
                    placeholder="At SPT Digital Marketing, we blend enterprise-level..."
                    rows={3}
                  />
                </div>
              </div>

              {/* Stats */}
              <div className="border-b pb-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold text-lg">Stats</h3>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => addArrayItem(setAbout, 'stats', { value: '', label: '' })}
                  >
                    Add Stat
                  </Button>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  {(about.stats || []).map((stat, idx) => (
                    <div key={idx} className="flex gap-2 items-start">
                      <div className="flex-1 grid grid-cols-2 gap-2">
                        <Input
                          value={stat.value || ''}
                          onChange={(e) => handleArrayChange(setAbout, 'stats', idx, 'value', e.target.value)}
                          placeholder="40+"
                        />
                        <Input
                          value={stat.label || ''}
                          onChange={(e) => handleArrayChange(setAbout, 'stats', idx, 'label', e.target.value)}
                          placeholder="Years Experience"
                        />
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => removeArrayItem(setAbout, 'stats', idx)}
                        className="text-red-500"
                      >
                        ×
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Expertise */}
              <div className="border-b pb-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold text-lg">Areas of Expertise</h3>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => addArrayItem(setAbout, 'expertise', { title: '', description: '' })}
                  >
                    Add Expertise
                  </Button>
                </div>
                <div className="space-y-4">
                  {(about.expertise || []).map((item, idx) => (
                    <div key={idx} className="flex gap-2 items-start p-4 bg-slate-50 rounded-lg">
                      <div className="flex-1 space-y-2">
                        <Input
                          value={item.title || ''}
                          onChange={(e) => handleArrayChange(setAbout, 'expertise', idx, 'title', e.target.value)}
                          placeholder="Brand Strategy"
                        />
                        <Input
                          value={item.description || ''}
                          onChange={(e) => handleArrayChange(setAbout, 'expertise', idx, 'description', e.target.value)}
                          placeholder="Strategic brand positioning that sets you apart"
                        />
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => removeArrayItem(setAbout, 'expertise', idx)}
                        className="text-red-500"
                      >
                        ×
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Experience/Career */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold text-lg">Professional Experience</h3>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => addArrayItem(setAbout, 'experience', { role: '', company: '', duration: '', highlights: '' })}
                  >
                    Add Experience
                  </Button>
                </div>
                <div className="space-y-4">
                  {(about.experience || []).map((item, idx) => (
                    <div key={idx} className="flex gap-2 items-start p-4 bg-slate-50 rounded-lg">
                      <div className="flex-1 space-y-2">
                        <div className="grid md:grid-cols-3 gap-2">
                          <Input
                            value={item.role || ''}
                            onChange={(e) => handleArrayChange(setAbout, 'experience', idx, 'role', e.target.value)}
                            placeholder="Job Title"
                          />
                          <Input
                            value={item.company || ''}
                            onChange={(e) => handleArrayChange(setAbout, 'experience', idx, 'company', e.target.value)}
                            placeholder="Company Name"
                          />
                          <Input
                            value={item.duration || ''}
                            onChange={(e) => handleArrayChange(setAbout, 'experience', idx, 'duration', e.target.value)}
                            placeholder="24+ Years"
                          />
                        </div>
                        <Textarea
                          value={item.highlights || ''}
                          onChange={(e) => handleArrayChange(setAbout, 'experience', idx, 'highlights', e.target.value)}
                          placeholder="Key accomplishments (one per line)"
                          rows={3}
                        />
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => removeArrayItem(setAbout, 'experience', idx)}
                        className="text-red-500"
                      >
                        ×
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <Button 
                onClick={() => saveContent('about', about)} 
                disabled={saving}
                className="bg-[#1F3C4F] hover:bg-[#152a38]"
              >
                {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save About Content
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SERVICES TAB */}
        <TabsContent value="services" className="mt-6">
          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle>Services Page Content</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Page Header */}
              <div className="border-b pb-6">
                <h3 className="font-semibold text-lg mb-4">Page Header</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Page Title</Label>
                    <Input
                      value={services.page_title}
                      onChange={(e) => setServices(prev => ({ ...prev, page_title: e.target.value }))}
                      placeholder="Services That Drive Results"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Badge Text</Label>
                    <Input
                      value={services.badge_text}
                      onChange={(e) => setServices(prev => ({ ...prev, badge_text: e.target.value }))}
                      placeholder="What We Do"
                    />
                  </div>
                </div>
                <div className="space-y-2 mt-4">
                  <Label>Page Subtitle</Label>
                  <Textarea
                    value={services.page_subtitle}
                    onChange={(e) => setServices(prev => ({ ...prev, page_subtitle: e.target.value }))}
                    placeholder="Comprehensive marketing services backed by 40+ years..."
                    rows={2}
                  />
                </div>
              </div>

              {/* Services List */}
              <div className="border-b pb-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold text-lg">Services</h3>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => addArrayItem(setServices, 'services_list', { 
                      title: '', 
                      description: '', 
                      icon: 'globe',
                      features: '' 
                    })}
                  >
                    Add Service
                  </Button>
                </div>
                <div className="space-y-4">
                  {(services.services_list || []).map((service, idx) => (
                    <div key={idx} className="flex gap-2 items-start p-4 bg-slate-50 rounded-lg">
                      <div className="flex-1 space-y-2">
                        <div className="grid md:grid-cols-2 gap-2">
                          <Input
                            value={service.title || ''}
                            onChange={(e) => handleArrayChange(setServices, 'services_list', idx, 'title', e.target.value)}
                            placeholder="Service Title"
                          />
                          <Input
                            value={service.icon || ''}
                            onChange={(e) => handleArrayChange(setServices, 'services_list', idx, 'icon', e.target.value)}
                            placeholder="Icon (globe, palette, trending-up, etc.)"
                          />
                        </div>
                        <Textarea
                          value={service.description || ''}
                          onChange={(e) => handleArrayChange(setServices, 'services_list', idx, 'description', e.target.value)}
                          placeholder="Service description"
                          rows={2}
                        />
                        <Textarea
                          value={service.features || ''}
                          onChange={(e) => handleArrayChange(setServices, 'services_list', idx, 'features', e.target.value)}
                          placeholder="Features (one per line)"
                          rows={3}
                        />
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => removeArrayItem(setServices, 'services_list', idx)}
                        className="text-red-500"
                      >
                        ×
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Process Steps */}
              <div className="border-b pb-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold text-lg">Process Steps</h3>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => addArrayItem(setServices, 'process_steps', { number: '', title: '', description: '' })}
                  >
                    Add Step
                  </Button>
                </div>
                <div className="space-y-4">
                  {(services.process_steps || []).map((step, idx) => (
                    <div key={idx} className="flex gap-2 items-start">
                      <div className="flex-1 grid grid-cols-4 gap-2">
                        <Input
                          value={step.number || ''}
                          onChange={(e) => handleArrayChange(setServices, 'process_steps', idx, 'number', e.target.value)}
                          placeholder="01"
                        />
                        <Input
                          value={step.title || ''}
                          onChange={(e) => handleArrayChange(setServices, 'process_steps', idx, 'title', e.target.value)}
                          placeholder="Discovery"
                          className="col-span-1"
                        />
                        <Input
                          value={step.description || ''}
                          onChange={(e) => handleArrayChange(setServices, 'process_steps', idx, 'description', e.target.value)}
                          placeholder="Description"
                          className="col-span-2"
                        />
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => removeArrayItem(setServices, 'process_steps', idx)}
                        className="text-red-500"
                      >
                        ×
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Industries */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold text-lg">Industries Served</h3>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => addArrayItem(setServices, 'industries', { name: '' })}
                  >
                    Add Industry
                  </Button>
                </div>
                <div className="grid md:grid-cols-2 gap-2">
                  {(services.industries || []).map((item, idx) => (
                    <div key={idx} className="flex gap-2">
                      <Input
                        value={item.name || ''}
                        onChange={(e) => handleArrayChange(setServices, 'industries', idx, 'name', e.target.value)}
                        placeholder="Home Improvement & Building Products"
                      />
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => removeArrayItem(setServices, 'industries', idx)}
                        className="text-red-500"
                      >
                        ×
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <Button 
                onClick={() => saveContent('services', services)} 
                disabled={saving}
                className="bg-[#1F3C4F] hover:bg-[#152a38]"
              >
                {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Services Content
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* CONTACT TAB */}
        <TabsContent value="contact" className="mt-6">
          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle>Contact Page Content</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Page Header */}
              <div className="border-b pb-6">
                <h3 className="font-semibold text-lg mb-4">Page Header</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Page Title</Label>
                    <Input
                      value={contact.page_title}
                      onChange={(e) => setContact(prev => ({ ...prev, page_title: e.target.value }))}
                      placeholder="Get in Touch"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Form Title</Label>
                    <Input
                      value={contact.form_title}
                      onChange={(e) => setContact(prev => ({ ...prev, form_title: e.target.value }))}
                      placeholder="Send Us a Message"
                    />
                  </div>
                </div>
                <div className="space-y-2 mt-4">
                  <Label>Page Subtitle</Label>
                  <Textarea
                    value={contact.page_subtitle}
                    onChange={(e) => setContact(prev => ({ ...prev, page_subtitle: e.target.value }))}
                    placeholder="Ready to elevate your marketing?..."
                    rows={2}
                  />
                </div>
              </div>

              {/* Contact Info */}
              <div className="border-b pb-6">
                <h3 className="font-semibold text-lg mb-4">Contact Information</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Location</Label>
                    <Input
                      value={contact.location}
                      onChange={(e) => setContact(prev => ({ ...prev, location: e.target.value }))}
                      placeholder="Southport, NC"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      value={contact.email}
                      onChange={(e) => setContact(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="info@sptdigitalmarketing.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      value={contact.phone}
                      onChange={(e) => setContact(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Response Time</Label>
                    <Input
                      value={contact.response_time}
                      onChange={(e) => setContact(prev => ({ ...prev, response_time: e.target.value }))}
                      placeholder="Within 24-48 hours"
                    />
                  </div>
                </div>
              </div>

              {/* Success Message */}
              <div className="border-b pb-6">
                <h3 className="font-semibold text-lg mb-4">Form Success Message</h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Success Title</Label>
                    <Input
                      value={contact.success_title}
                      onChange={(e) => setContact(prev => ({ ...prev, success_title: e.target.value }))}
                      placeholder="Thank You for Reaching Out!"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Success Message</Label>
                    <Textarea
                      value={contact.success_message}
                      onChange={(e) => setContact(prev => ({ ...prev, success_message: e.target.value }))}
                      placeholder="We've received your message and will get back to you..."
                      rows={3}
                    />
                  </div>
                </div>
              </div>

              {/* FAQs */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold text-lg">FAQs</h3>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => addArrayItem(setContact, 'faqs', { question: '', answer: '' })}
                  >
                    Add FAQ
                  </Button>
                </div>
                <div className="space-y-4">
                  {(contact.faqs || []).map((faq, idx) => (
                    <div key={idx} className="flex gap-2 items-start p-4 bg-slate-50 rounded-lg">
                      <div className="flex-1 space-y-2">
                        <Input
                          value={faq.question || ''}
                          onChange={(e) => handleArrayChange(setContact, 'faqs', idx, 'question', e.target.value)}
                          placeholder="What types of businesses do you work with?"
                        />
                        <Textarea
                          value={faq.answer || ''}
                          onChange={(e) => handleArrayChange(setContact, 'faqs', idx, 'answer', e.target.value)}
                          placeholder="Answer..."
                          rows={2}
                        />
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => removeArrayItem(setContact, 'faqs', idx)}
                        className="text-red-500"
                      >
                        ×
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <Button 
                onClick={() => saveContent('contact', contact)} 
                disabled={saving}
                className="bg-[#1F3C4F] hover:bg-[#152a38]"
              >
                {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Contact Content
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* FOOTER TAB */}
        <TabsContent value="footer" className="mt-6">
          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle>Footer & Global Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Company Tagline</Label>
                  <Input
                    value={footer.tagline}
                    onChange={(e) => setFooter(prev => ({ ...prev, tagline: e.target.value }))}
                    placeholder="Enterprise-level marketing expertise with a personal, collaborative approach."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Copyright Text</Label>
                  <Input
                    value={footer.copyright_text}
                    onChange={(e) => setFooter(prev => ({ ...prev, copyright_text: e.target.value }))}
                    placeholder="SPT Digital Marketing. All rights reserved."
                  />
                </div>
              </div>

              <Button 
                onClick={() => saveContent('footer', footer)} 
                disabled={saving}
                className="bg-[#1F3C4F] hover:bg-[#152a38]"
              >
                {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Footer Content
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SiteContentPage;
